/**
 * User Settings API
 *
 * Manages user notification and digest preferences via the API.
 */

import { fetchApi, getApiBase, getAuthHeaders } from './fetch';

export interface DigestTypePreferences {
  instructions: string | null;
}

export interface DigestPreferences {
  morning: DigestTypePreferences;
  evening: DigestTypePreferences;
  weekly: DigestTypePreferences;
}

export interface DigestTypePreferences {
  instructions: string | null;
}

export interface DigestPreferences {
  morning: DigestTypePreferences;
  evening: DigestTypePreferences;
  weekly: DigestTypePreferences;
}

export type AutoDecomposeMode = 'off' | 'suggest' | 'auto';
export type SortOption = 'due' | 'priority' | 'created' | 'title';
export type SortDirection = 'asc' | 'desc';
export type DigestVarietyMode = 'deterministic' | 'smart' | 'lessSmart' | 'chaos';

export interface UserSettings {
  dailyDigestEnabled: boolean;
  digestTime: string; // HH:MM
  digestTimezone: string | null;
  eveningDigestEnabled: boolean;
  eveningDigestTime: string;
  weeklyDigestEnabled: boolean;
  weeklyDigestDay: string;
  weeklyDigestTime: string;
  aiCostConfirmMode: 'always' | 'above_threshold' | 'never';
  aiCostThreshold: number;
  digestPreferences: DigestPreferences;
  autoDecompose: AutoDecomposeMode;
  defaultSort: SortOption;
  defaultSortDirection: SortDirection;
  digestVarietyMode: DigestVarietyMode;
}

export interface DigestSettings {
  dailyDigestEnabled: boolean;
  digestTime: string; // HH:MM
  digestTimezone: string | null;
}

export async function getUserSettings(): Promise<UserSettings> {
  return fetchApi<UserSettings>('/users/me/settings');
}

export interface UpdateDigestSettingsRequest {
  dailyDigestEnabled?: boolean;
  digestTime?: string;
  digestTimezone?: string | null;
}

export async function updateDigestSettings(
  data: UpdateDigestSettingsRequest,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>('/users/me/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export interface AiCostSettingsRequest {
  aiCostConfirmMode?: 'always' | 'above_threshold' | 'never';
  aiCostThreshold?: number;
}

export async function updateAiCostSettings(
  data: AiCostSettingsRequest,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>('/users/me/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getCaptureEmail(): Promise<{ address: string }> {
  return fetchApi<{ address: string }>('/users/me/capture-email');
}

export async function rotateCaptureEmail(): Promise<{ address: string }> {
  return fetchApi<{ address: string }>('/users/me/capture-email/rotate', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function initCaptureEmail(): Promise<{ address: string }> {
  return fetchApi<{ address: string }>('/users/me/capture-email/init', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export interface UpdateDigestPreferencesRequest {
  digestPreferences: Partial<DigestPreferences>;
}

export async function updateDigestPreferences(
  data: UpdateDigestPreferencesRequest,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>('/users/me/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function updateAutoDecomposeSetting(
  autoDecompose: AutoDecomposeMode,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>('/users/me/settings', {
    method: 'PATCH',
    body: JSON.stringify({ autoDecompose }),
  });
}

export async function updateSortSettings(
  defaultSort: SortOption,
  defaultSortDirection: SortDirection,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>('/users/me/settings', {
    method: 'PATCH',
    body: JSON.stringify({ defaultSort, defaultSortDirection }),
  });
}

export async function updateDigestVarietyMode(
  mode: DigestVarietyMode,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>('/users/me/settings', {
    method: 'PATCH',
    body: JSON.stringify({ digestVarietyMode: mode }),
  });
}

export interface UpdateWeeklyDigestSettingsRequest {
  weeklyDigestEnabled?: boolean;
  weeklyDigestDay?: string;
  weeklyDigestTime?: string;
}

export async function updateWeeklyDigestSettings(
  data: UpdateWeeklyDigestSettingsRequest,
): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>('/users/me/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export interface WeeklySummaryResponse {
  narrative: string;
  highlights: string[];
  completedCount: number;
  weekLabel: string;
}

export async function getWeeklySummary(): Promise<WeeklySummaryResponse | null> {
  try {
    return await fetchApi<WeeklySummaryResponse>('/ai/weekly-summary');
  } catch {
    return null;
  }
}

export class RateLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RateLimitError';
  }
}

export async function triggerDigest(
  type: 'morning' | 'evening' | 'weekly',
): Promise<{ sent: boolean }> {
  const apiBase = getApiBase();
  const authHeaders = getAuthHeaders();

  const response = await fetch(`${apiBase}/digest/${type}/trigger`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    if (response.status === 429) {
      throw new RateLimitError(
        `You've hit the 5/hour limit for ${type} test digests. Try again later.`,
      );
    }
    const errorData = await response.json().catch(() => ({}));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- errorData is unknown JSON from server
    const message = (errorData as any).message || `HTTP ${response.status}`;
    throw new Error(message);
  }

  return response.json();
}
